// Stimulus controller for collection filter interactions.
// Intercepts form submission to build clean, SEO-friendly URLs with comma-separated params.
import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    connect() {
        this.activePage = this.#currentPage()
        this.ignoreScrollUntil = 0
        this.scrollFrame = null
        this.restoreTimer = null

        this.handleScroll = this.#handleScroll.bind(this)
        this.handleMutations = this.#handleMutations.bind(this)
        this.handlePopState = this.#handlePopState.bind(this)
        this.handleFrameLoad = this.#handleFrameLoad.bind(this)
        this.mutationObserver = new MutationObserver(this.handleMutations)
        this.mutationObserver.observe(this.element, {childList: true, subtree: true})

        window.addEventListener("scroll", this.handleScroll, {passive: true})
        window.addEventListener("resize", this.handleScroll, {passive: true})
        window.addEventListener("popstate", this.handlePopState)
        this.element.addEventListener("turbo:frame-load", this.handleFrameLoad)
        this.#syncPageMarkers()
    }

    disconnect() {
        this.mutationObserver?.disconnect()
        window.removeEventListener("scroll", this.handleScroll)
        window.removeEventListener("resize", this.handleScroll)
        window.removeEventListener("popstate", this.handlePopState)
        this.element.removeEventListener("turbo:frame-load", this.handleFrameLoad)

        if (this.scrollFrame) {
            cancelAnimationFrame(this.scrollFrame)
        }

        if (this.restoreTimer) {
            clearTimeout(this.restoreTimer)
        }
    }

    // Triggered when any filter checkbox or sort select changes. The filter panel
    // lives outside the collection_list frame, so it stays open (with its badges
    // synced here) while the grid refreshes behind it.
    filterChange(event) {
        this.#syncFilterUI()
        this.#submitFilters()
    }

    // Triggered when a selected filter chip is clicked to remove it
    removeFilter(event) {
        event.preventDefault()
        const tag = event.currentTarget.dataset.tag
        if (!tag) return

        // Build the destination as if this control were unchecked, but leave
        // the live DOM untouched until after the visit. Turbo snapshots the
        // origin synchronously, so mutating first would make Back restore the
        // filtered URL with an unchecked drawer control.
        this.filterToUncheckAfterFrameLoad = tag
        this.#submitFilters({ excludingFilterId: tag })
    }

    // Category chips are real links so modified clicks and no-JS navigation
    // keep working. Plain clicks use the same collection frame as the strip,
    // preserving the document while child_nav_controller syncs tile state
    // from the completed destination URL.
    navigateCategory(event) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

        event.preventDefault()
        this.#visitCollection(event.currentTarget.href)
    }

    // Triggered by the Clear links (drawer header and chip row). The panel is
    // static, so its checkboxes must be reset client-side, not by a re-render.
    clearFilters(event) {
        event.preventDefault()

        // Visit first and defer the static-panel reset so Turbo snapshots the
        // filtered origin accurately for Back navigation. This also lets the
        // child-category strip resynchronize from the completed destination.
        this.resetFiltersAfterFrameLoad = true
        this.#visitCollection(window.location.pathname)
    }

    #resetFilterControls() {
        this.element.querySelectorAll('.collection-filter-menu input:checked').forEach(checkbox => {
            checkbox.checked = false
        })

        const sortSelect = this.element.querySelector('select[name="sort_order"]')
        if (sortSelect) sortSelect.value = 'best_sellers'

        // The completed frame normally removed the chip row already. Keep this
        // as a defensive cleanup if a custom frame payload left one behind.
        this.element.querySelector('.collection-applied-filters')?.remove()

        this.#syncFilterUI()
    }

    // Builds clean comma-separated URL params and updates the collection_list turbo frame.
    // Produces: ?tags=celtic,braided&metals=14K&sort=price_asc
    // Instead of: ?tags%5B%5D=113&tags%5B%5D=104&metals%5B%5D=14K&sort_order=best_sellers
    // Uses manual string building (not URLSearchParams) to avoid %2C encoding of commas.
    // The panel and sort control both live under this controller's element.
    #submitFilters({ excludingFilterId = null } = {}) {
        const parts = []

        const filterGroups = { 'tags[]': 'tags', 'p_tags[]': 'p_tags', 'metals[]': 'metals' }
        for (const [inputName, paramName] of Object.entries(filterGroups)) {
            const checked = Array.from(
                this.element.querySelectorAll(`input[name="${inputName}"]:checked`)
            ).filter(checkbox => checkbox.id !== excludingFilterId)
            if (checked.length > 0) {
                const values = checked.map(cb => cb.value).join(',')
                parts.push(`${paramName}=${values}`)
            }
        }

        const sortSelect = this.element.querySelector('select[name="sort_order"]')
        if (sortSelect && sortSelect.value && sortSelect.value !== 'best_sellers') {
            const sortMap = { 'best_sellers': 'best', 'price_asc': 'price_asc', 'price_desc': 'price_desc' }
            parts.push(`sort=${sortMap[sortSelect.value] || sortSelect.value}`)
        }

        // The child-category strip's ?cats= selection lives outside this
        // panel — carry it through, or filtering would silently unpick it.
        const cats = new URLSearchParams(window.location.search).get('cats')
        if (cats) parts.push(`cats=${cats}`)

        const baseUrl = window.location.pathname
        const url = parts.length > 0 ? `${baseUrl}?${parts.join('&')}` : baseUrl

        this.#visitCollection(url)
    }

    #visitCollection(url) {
        // The drawer's count line and results button hold stale numbers until
        // the frame answers; mark them so they read as pending, not final.
        this.#setDrawerRefreshing(true)
        Turbo.visit(url, { frame: "collection_list", action: "advance" })
    }

    #setDrawerRefreshing(refreshing) {
        [document.getElementById('collectionDrawerCount'),
         document.getElementById('collectionShowResults')].forEach(el => {
            el?.classList.toggle('is-refreshing', refreshing)
        })
    }

    // The static panel's badges, is-active states, and Clear link reflect the
    // checkbox state, which only this controller changes.
    #syncFilterUI() {
        let total = 0

        this.element.querySelectorAll('.collection-filter-group').forEach(group => {
            const summary = group.querySelector('summary')
            const count = group.querySelectorAll('.collection-filter-menu input:checked').length
            total += count

            summary.classList.toggle('is-active', count > 0)
            let badge = summary.querySelector('.collection-filter-badge')
            if (count > 0) {
                if (!badge) {
                    badge = document.createElement('span')
                    badge.className = 'collection-filter-badge'
                    summary.appendChild(badge)
                }
                badge.textContent = count
            } else {
                badge?.remove()
            }
        })

        document.getElementById('collectionFilterClear')?.classList.toggle('d-none', total === 0)
    }

    // Mirrors fresh server-rendered data into the static panel after each frame
    // refresh: the drawer's count line, results button, and per-option facets.
    #handleFrameLoad(event) {
        if (event.target.id !== 'collection_list') return

        const chrome = event.target.querySelector('template[data-collection-chrome]')
        if (chrome) this.#applyPageChrome(chrome)

        if (this.resetFiltersAfterFrameLoad) {
            this.#resetFilterControls()
            this.resetFiltersAfterFrameLoad = false
        }

        if (this.filterToUncheckAfterFrameLoad) {
            const checkbox = document.getElementById(this.filterToUncheckAfterFrameLoad)
            if (checkbox) checkbox.checked = false
            this.filterToUncheckAfterFrameLoad = null
            this.#syncFilterUI()
        }

        // Apply count state after page chrome. Cross-collection visits replace
        // the drawer, so updating the outgoing drawer first is wasted work.
        this.#setDrawerRefreshing(false)

        const count = this.element.querySelector('.collection-sort-count')
        if (count) {
            const button = document.getElementById('collectionShowResults')
            if (button) button.textContent = `Show ${count.textContent.trim()}`

            const drawerCount = document.getElementById('collectionDrawerCount')
            if (drawerCount) drawerCount.textContent = count.textContent.trim()
        }

        // Apply counts after page chrome: a cross-collection visit replaces
        // the whole drawer, while a same-collection filter visit preserves it.
        const facets = event.target.querySelector('[data-collection-facets]')
        if (facets) this.#applyFacetCounts(JSON.parse(facets.textContent))
    }

    // In-frame navigation can land on a different collection (the child_nav
    // strip) or a filtered variant, so frame responses ship the destination
    // shell — visible chrome, filter drawer, and serialized head
    // state — as an inert <template>. Same-collection filter visits deliberately
    // keep the live drawer so its open/scroll state survives.
    #applyPageChrome(template) {
        const incomingCollectionId = template.dataset.collectionId
        const collectionChanged = Boolean(
            incomingCollectionId && incomingCollectionId !== this.element.dataset.collectionId
        )

        for (const id of ["collection-breadcrumb", "collection-header"]) {
            const fresh = template.content.getElementById(id)
            const current = document.getElementById(id)
            if (!fresh || !current) continue
            const node = document.importNode(fresh, true)
            this.#promoteHeading(node)
            current.replaceWith(node)
        }
        this.#applyDescription(template)

        if (collectionChanged) {
            const freshPanel = template.content.getElementById("filterOffcanvas")
            const currentPanel = document.getElementById("filterOffcanvas")
            if (freshPanel && currentPanel) {
                currentPanel.replaceWith(document.importNode(freshPanel, true))
            }
            this.element.dataset.collectionId = incomingCollectionId
        }

        this.#applyHeadState(template)
        template.remove()
    }

    // The destination's description (ranking copy + FAQ) rides in
    // application/json rather than markup, so its H2/H3s and
    // #collection-description appear in the raw source exactly once.
    #applyDescription(template) {
        const payload = template.content.querySelector("[data-collection-description]")
        if (!payload) return

        let html
        try {
            html = JSON.parse(payload.textContent)
        } catch (error) {
            console.error("Invalid collection description payload", error)
            return
        }

        const holder = document.createElement("template")
        holder.innerHTML = html
        const fresh = holder.content.getElementById("collection-description")
        const current = document.getElementById("collection-description")
        if (fresh && current) current.replaceWith(fresh)
    }

    // The chrome template ships its heading as a <div> so each URL's source
    // contains exactly one literal <h1>. Restore the real tag before the
    // node goes live, or the page loses its h1 after in-frame navigation.
    #promoteHeading(node) {
        const demoted = node.querySelector("[data-collection-heading]")
        if (!demoted || demoted.tagName === "H1") return
        const h1 = document.createElement("h1")
        for (const { name, value } of demoted.attributes) h1.setAttribute(name, value)
        h1.append(...demoted.childNodes)
        demoted.replaceWith(h1)
    }

    #applyHeadState(template) {
        const payload = template.content.querySelector("[data-collection-head-state]")
        if (!payload) return

        let state
        try {
            state = JSON.parse(payload.textContent)
        } catch (error) {
            console.error("Invalid collection head state", error)
            return
        }

        if (state.title) document.title = state.title

        this.#syncCanonical(state.canonical)
        this.#syncMeta("name", "description", state.description)
        this.#syncMeta("name", "keywords", state.keywords)
        this.#syncMeta("name", "robots", state.robots)

        this.#syncMeta("property", "og:title", state.open_graph?.title)
        this.#syncMeta("property", "og:description", state.open_graph?.description)
        this.#syncMeta("property", "og:url", state.open_graph?.url)
        this.#syncMeta("property", "og:type", state.open_graph?.type)
        this.#syncMeta("property", "og:site_name", state.open_graph?.site_name)
        this.#syncMeta("property", "og:image", state.open_graph?.image)
        this.#syncMeta("property", "og:image:type", state.open_graph?.image_type)
        this.#syncMeta("property", "og:image:width", state.open_graph?.image_width)
        this.#syncMeta("property", "og:image:height", state.open_graph?.image_height)
        this.#syncMeta("property", "og:image:alt", state.open_graph?.image_alt)

        this.#syncMeta("name", "twitter:card", state.twitter?.card)
        this.#syncMeta("name", "twitter:title", state.twitter?.title)
        this.#syncMeta("name", "twitter:description", state.twitter?.description)
        this.#syncMeta("name", "twitter:image", state.twitter?.image)
        this.#syncMeta("name", "twitter:image:alt", state.twitter?.image_alt)

        this.#syncJsonLd("collection-breadcrumb-schema", state.breadcrumb_schema)
        this.#syncJsonLd("collection-item-list-schema", state.item_list_schema)
        this.#syncJsonLd("collection-faq-schema", state.faq_schema)
    }

    #syncCanonical(href) {
        let canonical = document.head.querySelector('link[rel="canonical"]')
        if (!href) {
            canonical?.remove()
            return
        }

        if (!canonical) {
            canonical = document.createElement("link")
            canonical.rel = "canonical"
            document.head.appendChild(canonical)
        }
        canonical.href = href
    }

    #syncMeta(attribute, key, content) {
        const selector = `meta[${attribute}="${key}"]`
        let meta = document.head.querySelector(selector)
        if (content === null || content === undefined || content === "") {
            meta?.remove()
            return
        }

        if (!meta) {
            meta = document.createElement("meta")
            meta.setAttribute(attribute, key)
            document.head.appendChild(meta)
        }
        meta.setAttribute("content", content)
    }

    #syncJsonLd(id, data) {
        const current = document.getElementById(id)
        if (!data) {
            current?.remove()
            return
        }

        const script = document.createElement("script")
        script.id = id
        script.type = "application/ld+json"
        script.textContent = JSON.stringify(data)

        if (current) {
            current.replaceWith(script)
        } else {
            document.head.appendChild(script)
        }
    }

    // The server recomputes option counts within the filtered set on every
    // response; mirror them into the panel (hiding zero-result options) without
    // re-rendering it, so open menus and scroll position survive. Checked
    // options stay visible regardless so they can always be unchecked.
    #applyFacetCounts(counts) {
        this.element.querySelectorAll('.collection-filter-group').forEach(group => {
            let visible = 0

            group.querySelectorAll('.collection-filter-menu input[type="checkbox"]').forEach(input => {
                const option = input.closest('.form-check')
                const label = option?.querySelector('.form-check-label')
                const count = counts[input.id]
                const show = count !== undefined || input.checked

                option?.classList.toggle('d-none', !show)
                if (show) visible += 1
                if (label && count !== undefined) {
                    label.textContent = label.textContent.replace(/\(\d+\)\s*$/, `(${count})`)
                }
            })

            group.classList.toggle('d-none', visible === 0)
        })
    }

    #handleMutations() {
        this.#syncPageMarkers()
    }

    #syncPageMarkers() {
        this.pageMarkers = Array.from(
            this.element.querySelectorAll("[data-collection-page-url]")
        )
        this.#schedulePageUrlUpdate()
    }

    #handleScroll() {
        this.#schedulePageUrlUpdate()
    }

    #handlePopState() {
        this.activePage = this.#currentPage()
        this.ignoreScrollUntil = Date.now() + 500

        if (this.restoreTimer) {
            clearTimeout(this.restoreTimer)
        }

        this.restoreTimer = setTimeout(() => {
            this.ignoreScrollUntil = 0
            this.#schedulePageUrlUpdate()
        }, 500)
    }

    #schedulePageUrlUpdate() {
        if (this.scrollFrame) return

        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null
            this.#updatePageUrl()
        })
    }

    #updatePageUrl() {
        if (!this.pageMarkers?.length || Date.now() < this.ignoreScrollUntil) return

        const activationLine = window.innerHeight * 0.35
        let activeMarker = this.pageMarkers[0]

        for (const marker of this.pageMarkers) {
            if (marker.getBoundingClientRect().top <= activationLine) {
                activeMarker = marker
            } else {
                break
            }
        }

        const url = activeMarker.dataset.collectionPageUrl
        const page = Number(activeMarker.dataset.collectionPage)
        if (!url || page === this.activePage) return

        const state = {
            ...(window.history.state || {}),
            collectionPage: page
        }

        window.history.pushState(state, "", url)
        this.activePage = page
    }

    #currentPage() {
        const page = Number(new URLSearchParams(window.location.search).get("page"))
        return page > 1 ? page : 1
    }
}
